import type { Verdict } from './types';

export interface VerdictMeta {
  label: string;
  /** Mantine color name. */
  color: 'green' | 'yellow' | 'red' | 'gray';
}

/** PRESENTATION ONLY — the verdict itself always comes from the backend. */
export const VERDICT_META: Record<Verdict, VerdictMeta> = {
  // NOT "not enough data" — that blames the instrument for what is a fact
  // about the network. The verdict gates on `measured < min_eligible_buckets`,
  // and `measured` counts only minutes that cleared the packet floor, so this
  // is precisely "too few minutes carried real traffic". On a normally quiet
  // network that is the expected reading, not a fault: crm/capacity.py:527
  // records VLAN 1161 sampling 3694 minutes and measuring 689.
  //
  // A dead sampler produces this verdict too, but it is no longer confusable
  // with a quiet network: the Stale badge and the sampled-minute count say
  // which one you are looking at.
  insufficient_data: { label: 'Not enough traffic', color: 'gray' },
  healthy: { label: 'Healthy', color: 'green' },
  occasionally_at_capacity: { label: 'Occasionally at capacity', color: 'yellow' },
  frequently_at_capacity: { label: 'Frequently at capacity', color: 'red' },
};

/**
 * Presentation for a verdict, tolerating one the wire invented.
 *
 * The fallback is deliberate — a deployed bundle can outlive the backend that
 * serves it — but it is NOT silent. A backend that adds a verdict more severe
 * than `frequently_at_capacity` would otherwise render as a grey "Not enough
 * data" badge on the worst network in the estate, with nothing anywhere
 * saying so.
 */
export const verdictMeta = (verdict: string): VerdictMeta => {
  const meta = VERDICT_META[verdict as Verdict];
  if (!meta) {
    console.warn(`[capacity] unknown verdict from backend: ${verdict}`);
    return VERDICT_META.insufficient_data;
  }
  return meta;
};
